import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { testApi } from "@/api/testApi";
import { CertificateContent } from "@/utils/certification";
import {
  certificateDownloadHint,
  downloadCertificatePdf,
} from "@/utils/certificateDownload";
import { Loader2 } from "lucide-react";

/**
 * Same-page certificate preview. Avoids window.open / popup blockers.
 * Download PDF fetches a server-rendered file instead of using window.print().
 */
export default function CertificateModal({ courseId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [certificationData, setCertificationData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  useEffect(() => {
    if (!open || !courseId) {
      return;
    }

    let cancelled = false;

    const fetchCertification = async () => {
      try {
        setLoading(true);
        setErrorMessage("");
        setCertificationData(null);
        const response = await testApi.certification(courseId);

        if (cancelled) return;

        if (response.success && response.data?.eligible) {
          setCertificationData(response.data);
        } else {
          setErrorMessage(
            response.data?.message ||
              "You must pass the assessment with the required score to view your certificate.",
          );
        }
      } catch (error) {
        console.error("Failed to load certificate:", error);
        if (!cancelled) {
          setErrorMessage("Failed to load certificate. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCertification();
    return () => {
      cancelled = true;
    };
  }, [open, courseId]);

  useEffect(() => {
    if (!open) setDownloadError("");
  }, [open]);

  const handleDownload = async () => {
    if (!certificationData || downloading) return;
    setDownloading(true);
    setDownloadError("");
    try {
      await downloadCertificatePdf(courseId);
    } catch (error) {
      console.error("Certificate download failed:", error);
      setDownloadError("Couldn't download the certificate. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="certificate-modal-dialog max-h-[95vh] w-[min(100vw-1rem,210mm)] max-w-[210mm] overflow-y-auto p-0 sm:max-w-[210mm]"
        showCloseButton
      >
        <div className="sticky top-0 z-10 flex flex-col gap-3 border-b border-gray-200 bg-[#f6fafc] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="text-base text-[#2B6A7F]">
              Certificate
            </DialogTitle>
            <DialogDescription
              className={`text-xs ${downloadError ? "text-red-600" : "text-gray-600"}`}
            >
              {downloadError || certificateDownloadHint()}
            </DialogDescription>
          </DialogHeader>
          <Button
            type="button"
            className="shrink-0 rounded-full bg-[#5D93A5] px-6 text-white hover:bg-[#4a7a8a]"
            disabled={loading || !certificationData || downloading}
            onClick={handleDownload}
          >
            {downloading ? "Preparing…" : "Download PDF"}
          </Button>
        </div>

        <div className="px-2 py-4 sm:px-4">
          {loading ? (
            <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 text-[#2B6A7F]">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-sm">Loading certificate…</p>
            </div>
          ) : errorMessage ? (
            <div className="px-6 py-10 text-center">
              <h2 className="mb-2 text-lg font-semibold text-[#2B6A7F]">
                Certificate Unavailable
              </h2>
              <p className="text-gray-700">{errorMessage}</p>
            </div>
          ) : certificationData ? (
            <CertificateContent data={certificationData} />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
